import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom'; // Outlet renders the matched child route element
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import ThemeSidebar from './ThemeSidebar';
import styles from './DashboardLayout.module.css';
import useReferralBonus from '../../hooks/useReferralBonus';
import ReferralBonusModal from '../UI/ReferralBonusModal';
import { useFormStep } from '../../context/FormStepContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthToken = () => {
  return localStorage.getItem('authToken') ||
         localStorage.getItem('idToken') ||
         localStorage.getItem('refreshToken') ||
         localStorage.getItem('token') ||
         sessionStorage.getItem('token');
};

const DashboardLayout = () => {
  // Use the referral bonus hook
  const { showBonusModal, closeBonusModal, bonusDetails } = useReferralBonus();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formStep, listingMode, showVariantsTab, isThemesPanelOpen, setIsThemesPanelOpen, showResultPopup, showBothSidebars, setShowBothSidebars, isFontsPanelOpen, setIsFontsPanelOpen } = useFormStep();
  const finalizeCalledRef = useRef(false);

  // One-time Amazon finalize: triggered only when redirected back with ?amazon_connected=true
  useEffect(() => {
    if (searchParams.get('amazon_connected') && !finalizeCalledRef.current) {
      finalizeCalledRef.current = true;

      // Clean up the URL parameter immediately
      searchParams.delete('amazon_connected');
      setSearchParams(searchParams, { replace: true });

      // Call finalize API once in the background
      const token = getAuthToken();
      if (token) {
        fetch(`${API_URL}/auth/amazon/finalize`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }).catch(() => {});
      }
    }
  }, [searchParams, setSearchParams]);
  
  // Check if we're on the infographics tab in single or bulk listing generator
  // Infographics step depends on whether Variants tab is enabled.
  // - Without Variants: Infographics is step 4 (new) / step 5 (existing)
  // - With Variants:    Variants is step 4 (new) / step 5 (existing), Infographics shifts to step 5 (new) / step 6 (existing)
  // Don't show ThemeSidebar when results are showing
  const infographicsStep = showVariantsTab
    ? (listingMode === 'new' ? 5 : 6)
    : (listingMode === 'new' ? 4 : 5);

  const isSingleListingPage = location.pathname.includes('/dashboard/generate/single');
  const isBulkListingPage = location.pathname.includes('/dashboard/generate/bulk');

  const isInfographicsTab =
    !showResultPopup &&
    (isSingleListingPage || isBulkListingPage) &&
    formStep === infographicsStep;
  
  // Close themes panel when results are showing
  useEffect(() => {
    if (showResultPopup && isThemesPanelOpen) {
      setIsThemesPanelOpen(false);
    }
  }, [showResultPopup, isThemesPanelOpen, setIsThemesPanelOpen]);

  // Close fonts panel when results are showing
  useEffect(() => {
    if (showResultPopup && isFontsPanelOpen) {
      setIsFontsPanelOpen(false);
    }
  }, [showResultPopup, isFontsPanelOpen, setIsFontsPanelOpen]);

  // Reset showBothSidebars when navigating away from infographics tab
  useEffect(() => {
    if (!isInfographicsTab && showBothSidebars) {
      setShowBothSidebars(false);
    }
  }, [isInfographicsTab, showBothSidebars, setShowBothSidebars]);

  // Close themes panel when navigating away from infographics tab
  useEffect(() => {
    if (!isInfographicsTab && isThemesPanelOpen) {
      setIsThemesPanelOpen(false);
    }
  }, [isInfographicsTab, isThemesPanelOpen, setIsThemesPanelOpen]);

  // Close fonts panel when navigating away from infographics tab
  useEffect(() => {
    if (!isInfographicsTab && isFontsPanelOpen) {
      setIsFontsPanelOpen(false);
    }
  }, [isInfographicsTab, isFontsPanelOpen, setIsFontsPanelOpen]);
  
  const handleThemesToggle = (isOpen) => {
    setIsThemesPanelOpen(isOpen);
  };
  
  return (
    <div className={styles.dashboardLayout}>
      {showBothSidebars && isInfographicsTab ? (
        <>
          <Sidebar showBothSidebars={true} />
          <ThemeSidebar onThemesToggle={handleThemesToggle} isBulk={isBulkListingPage} />
        </>
      ) : isInfographicsTab ? (
        <ThemeSidebar onThemesToggle={handleThemesToggle} isBulk={isBulkListingPage} />
      ) : (
      <Sidebar />
      )}
      <div className={`${styles.mainContent} ${(isThemesPanelOpen || isFontsPanelOpen) && isInfographicsTab ? styles.mainContentWithPanel : ''} ${showBothSidebars && isInfographicsTab ? styles.mainContentWithBothSidebars : ''}`}>
        <Navbar />
        <div className={styles.pageContent}>
          <Outlet /> {/* Child routes defined in App.js will render here */}
        </div>
      </div>

      {/* Referral Bonus Modal */}
      <ReferralBonusModal
        show={showBonusModal}
        onClose={closeBonusModal}
        creditsAdded={bonusDetails.creditsAdded}
        tokensAdded={bonusDetails.tokensAdded}
      />
    </div>
  );
};

export default DashboardLayout;